import {CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition} from "@angular/cdk/overlay"
import {
    Component,
    computed,
    DestroyRef,
    inject,
    OnInit,
    signal,
    viewChild,
    viewChildren,
    afterNextRender,
    Injector,
    ChangeDetectionStrategy,
} from "@angular/core"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {MatDialog, MatDialogRef} from "@angular/material/dialog"
import {MatMenuModule} from "@angular/material/menu"
import {MatTooltip} from "@angular/material/tooltip"
import {ActivatedRoute, ParamMap, Router} from "@angular/router"
import {NotificationsService} from "@app/common/services/notifications/notifications.service"
import {PreviewSceneComponent} from "@app/material-editor/components/misc/preview-scene/preview-scene.component"
import {MaterialGraphChangeService} from "@app/material-editor/services/material-graph-change.service"
import {PreviewSceneService} from "@app/material-editor/services/preview-scene.service"
import {MaterialEditorEntryMode} from "@app/material-editor/types/entry-mode"
import {SocketConnectionsService} from "@app/socket-editor/services/socket-connections.service"
import {deserializeMaterialGraph, AddableNode, addNodeSections, materialConnection, MaterialGraph, MaterialNode, graphSchemaString} from "@cm/material-nodes"
import {IsDefined, IsNonNull} from "@cm/utils/filter"
import {RoutedDialogComponent} from "@common/components/dialogs/routed-dialog/routed-dialog.component"
import {ListItemComponent} from "@common/components/item"
import {ToggleComponent} from "@common/components/buttons/toggle/toggle.component"
import {fetchIgnoringErrors, fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {DialogSize} from "@common/models/dialogs"
import {Settings} from "@common/models/settings/settings"
import {OrganizationsService} from "@common/services/organizations/organizations.service"
import {UtilsService} from "@legacy/helpers/utils"
import {
    MaterialEditorCreateMaterialRevisionGQL,
    MaterialEditorDataGQL,
    OrganizationsWithNodeEditorTemplateIdGQL,
} from "@material-editor/components/material-editor/material-editor.generated"
import {AddMenuSectionComponent} from "@material-editor/components/misc/add-menu-section/add-menu-section.component"
import {
    SaveMaterialRevisionDialogComponent,
    SaveMaterialRevisionDialogResult,
} from "@material-editor/components/misc/save-material-revision-dialog/save-material-revision-dialog.component"
import {TitleBarComponent} from "@material-editor/components/misc/title-bar/title-bar.component"
import {MaterialNodeComponent} from "@material-editor/components/nodes/material-node/material-node.component"
import {ScreenSizes} from "@material-editor/models/screen-sizes"
import {MaterialEditorData} from "@material-editor/types/material-editor"
import {NodeEditorComponent} from "@node-editor/components/node-editor/node-editor.component"
import {NodesService} from "@node-editor/services/nodes.service"
import {combineLatest, firstValueFrom} from "rxjs"
import {z} from "zod"
import {ButtonComponent} from "@common/components/buttons/button/button.component"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-material-editor",
    templateUrl: "./material-editor.component.html",
    styleUrls: ["./material-editor.component.scss"],
    imports: [
        RoutedDialogComponent,
        NodeEditorComponent,
        MatMenuModule,
        MatTooltip,
        CdkConnectedOverlay,
        CdkOverlayOrigin,
        TitleBarComponent,
        AddMenuSectionComponent,
        PreviewSceneComponent,
        ListItemComponent,
        MaterialNodeComponent,
        ButtonComponent,
        ToggleComponent,
    ],
    providers: [MaterialGraphChangeService, PreviewSceneService],
})
export class MaterialEditorComponent implements OnInit {
    screenSizes = ScreenSizes
    readonly $screenSize = signal<ScreenSizes | undefined>(undefined)
    dialogSizes = DialogSize
    readonly $addNodeSections = signal(addNodeSections)
    readonly $showAllNodes = signal(false)
    readonly $allowSave = signal(true)
    readonly $addNodesMenuOpen = signal(false)
    readonly addNodesMenuPosition: ConnectedPosition[] = [{originX: "start", originY: "bottom", overlayX: "start", overlayY: "top", offsetY: 10}]
    returnUrl: {url: string; relative: boolean} = {url: "../../", relative: true}

    private readonly $savedHash = signal("")
    private readonly graphChangeService = inject(MaterialGraphChangeService)

    private readonly $currentHash = computed(() => {
        this.graphChangeService.$graphVersion()
        return this.$data()?.materialGraph?.uniqueId ?? ""
    })

    readonly $isDirty = computed(() => {
        const data = this.$data()
        if (data && !data.activeRevision) return true
        const saved = this.$savedHash()
        const current = this.$currentHash()
        return saved !== "" && saved !== current
    })

    readonly $data = signal<MaterialEditorData | null | undefined>(undefined)

    readonly $nodeEditor = viewChild.required(NodeEditorComponent)
    readonly $routedDialog = viewChild.required(RoutedDialogComponent)

    readonly $materialNodes = viewChildren(MaterialNodeComponent)

    readonly organizations = inject(OrganizationsService)

    readonly organizationsWithNodeEditorTemplateId = inject(OrganizationsWithNodeEditorTemplateIdGQL)
    readonly materialEditorData = inject(MaterialEditorDataGQL)
    readonly materialEditorCreateMaterialRevision = inject(MaterialEditorCreateMaterialRevisionGQL)

    readonly nodesService = inject(NodesService<MaterialNode>)
    readonly connectionsService = inject(SocketConnectionsService)
    readonly notifications = inject(NotificationsService)
    readonly previewSceneService = inject(PreviewSceneService)

    destroyRef = inject(DestroyRef)
    private readonly injector = inject(Injector)
    router = inject(Router)
    route = inject(ActivatedRoute)
    dialog = inject(MatDialog)
    utils = inject(UtilsService)

    ngOnInit() {
        combineLatest([this.route.paramMap, this.route.data])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(async ([paramMap, _data]) => {
                const loadedData = await this.loadData(paramMap)
                this.$data.set({
                    activeRevision: loadedData.activeRevision ?? null,
                    cyclesRevisions: loadedData.cyclesRevisions?.filter(IsDefined) ?? [],
                    materialGraph: loadedData.materialGraph,
                    materialId: loadedData.materialId,
                    materialName: loadedData.materialName,
                    nodeEditorTemplateId: loadedData.nodeEditorTemplateId,
                })
                await this.updateNodesAndConnections(loadedData.materialGraph)
                // Connect service to graph and capture initial hash
                if (loadedData.materialGraph) {
                    this.graphChangeService.connectToGraph(loadedData.materialGraph)
                }
                this.$savedHash.set(loadedData.materialGraph?.uniqueId ?? "")
                this.graphChangeService.$graphVersion.update((c) => c + 1)
            })

        this.nodesService.onAdd$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((addedNodes) => {
            let dirty = false
            for (const addedNode of addedNodes) {
                this.$data()?.materialGraph?.addNode(addedNode)
                dirty = true
            }
            if (dirty) {
                this.previewSceneService.$refresh.next()
            }
        })
        this.nodesService.onDelete$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((deletedNodes) => {
            let dirty = false
            for (const deletedNode of deletedNodes) {
                this.$data()?.materialGraph?.removeNode(deletedNode)
                dirty = true
            }
            if (dirty) {
                this.previewSceneService.$refresh.next()
            }
        })
        this.connectionsService.onAdd$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((addedConnections) => {
            let dirty = false
            for (const addedConnection of addedConnections) {
                if (addedConnection.source.nodeId && addedConnection.destination.nodeId) {
                    const sourceNode = this.nodesService.get(addedConnection.source.nodeId)
                    const destinationNode = this.nodesService.get(addedConnection.destination.nodeId)
                    const materialGraph = this.$data()?.materialGraph
                    if (materialGraph && sourceNode && destinationNode && addedConnection.source.socketId && addedConnection.destination.socketId) {
                        if (materialGraph.connections.some((connection) => connection.id === addedConnection.id)) {
                            // connection already exists, skip
                        } else {
                            const connection = materialConnection(sourceNode, addedConnection.source.socketId)
                            destinationNode.updateInput(addedConnection.destination.socketId, connection)
                            materialGraph?.addConnection(connection)
                            dirty = true
                        }
                    }
                }
            }
            if (dirty) {
                this.previewSceneService.$refresh.next()
            }
        })
        this.connectionsService.onDelete$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((deletedConnections) => {
            let dirty = false
            for (const deletedConnection of deletedConnections) {
                if (
                    deletedConnection.source.nodeId &&
                    deletedConnection.destination.nodeId &&
                    deletedConnection.source.socketId &&
                    deletedConnection.destination.socketId
                ) {
                    const materialGraph = this.$data()?.materialGraph

                    // Find the connection by matching source and destination, not by ID
                    // (UI connection IDs are different from MaterialGraph connection IDs)
                    const existingConnection = materialGraph?.connections.find(
                        (connection) =>
                            connection.data.source.nodeId === deletedConnection.source.nodeId &&
                            connection.data.source.socketId === deletedConnection.source.socketId &&
                            connection.data.destination.nodeId === deletedConnection.destination.nodeId &&
                            connection.data.destination.socketId === deletedConnection.destination.socketId,
                    )

                    if (existingConnection && materialGraph) {
                        materialGraph.removeConnection(existingConnection)
                        dirty = true
                    }
                }
            }
            if (dirty) {
                this.previewSceneService.$refresh.next()
            }
        })
    }

    async updateNodesAndConnections(materialGraph?: MaterialGraph) {
        this.nodesService.synchronize([...(materialGraph?.nodes ?? [])])
        // give Angular some time to render the nodes before we try to get the socket positions
        setTimeout(async () => {
            const connectionsWithPositions = await Promise.all(
                (materialGraph?.connections ?? []).map(async (connection) => {
                    const data = connection.data
                    const sourcePosition = await this.getSocketPosition(data.source.nodeId, data.source.socketId, "output").catch(() => null)
                    const destinationPosition = await this.getSocketPosition(data.destination.nodeId, data.destination.socketId, "input").catch(() => null)
                    if (!sourcePosition || !destinationPosition) {
                        return null
                    }
                    return {
                        id: data.id,
                        source: {
                            ...data.source,
                            position: sourcePosition,
                            refreshPosition: () => this.getSocketPosition(data.source.nodeId, data.source.socketId, "output").catch(() => sourcePosition),
                        },
                        destination: {
                            ...data.destination,
                            position: destinationPosition,
                            refreshPosition: () =>
                                this.getSocketPosition(data.destination.nodeId, data.destination.socketId, "input").catch(() => destinationPosition),
                        },
                    }
                }),
            )
            this.connectionsService.synchronize(connectionsWithPositions.filter(IsDefined))
            // a socket that gains a connection changes height, which moves every position measured below it
            afterNextRender(() => void this.connectionsService.refreshSocketPositions(), {injector: this.injector})
        })
    }

    async loadData(paramMap: ParamMap): Promise<MaterialEditorData> {
        const materialId = paramMap.get("itemId")
        if (!materialId) {
            throw new Error("No material id provided in url")
        }
        const entryMode = z.nativeEnum(MaterialEditorEntryMode).parse(this.route.snapshot.data["entryMode"])
        switch (entryMode) {
            case MaterialEditorEntryMode.Edit: {
                const materialRevisionId = paramMap.get("revisionId") ?? undefined
                const {materialEditorData: material} = await fetchIgnoringErrors(this.materialEditorData)({materialId, materialRevisionId})
                if (!material.revision) {
                    throw new Error("No material revision found")
                }
                const graphNode = material.revision.graphNode
                const materialGraph = graphNode ? deserializeMaterialGraph(graphNode) : undefined
                materialGraph?.applyDefaults()
                return {
                    activeRevision: material.revision,
                    cyclesRevisions: material.revisions ?? [],
                    materialGraph,
                    materialId,
                    materialName: material.name ?? null,
                    nodeEditorTemplateId: await this.loadNodeEditorTemplateIds(),
                }
            }
            case MaterialEditorEntryMode.Setup: {
                if (!materialId) {
                    throw new Error("No material id provided in url")
                }
                const textureSetRevisionId = paramMap.get("textureSetRevisionId") ?? ""
                if (textureSetRevisionId) {
                    return {
                        activeRevision: null,
                        cyclesRevisions: [],
                        materialGraph: MaterialGraph.forTextureSetRevision(textureSetRevisionId),
                        materialId: materialId,
                        materialName: "New material",
                        nodeEditorTemplateId: await this.loadNodeEditorTemplateIds(),
                    }
                }
                return {
                    activeRevision: null,
                    cyclesRevisions: [],
                    materialGraph: MaterialGraph.empty(),
                    materialId: materialId,
                    materialName: "New material",
                    nodeEditorTemplateId: await this.loadNodeEditorTemplateIds(),
                }
            }
        }
    }

    async loadNodeEditorTemplateIds() {
        const ownOrganizations = await this.organizations.own
        if (ownOrganizations) {
            return fetchThrowingErrors(this.organizationsWithNodeEditorTemplateId)({
                organizationIds: ownOrganizations.map((organization) => organization.id),
            }).then((data) => {
                return data.organizations.map((organization) => organization?.nodeEditorTemplate?.legacyId).filter(IsNonNull)?.[0] ?? null
            })
        }
        return null
    }

    getSocketPosition = async (nodeId: string, socketId: string, direction: "input" | "output") => {
        const sourceNode = this.$materialNodes().find((node) => node.$material().id === nodeId)
        if (!sourceNode) {
            throw new Error("Failed to find source node for connection")
        }
        await sourceNode.waitForInit
        const sourcePosition = sourceNode.getSocketPosition(socketId, direction)
        if (!sourcePosition) {
            throw new Error(`Failed to find source node position for connection: ${nodeId}, ${socketId}, ${sourceNode}`)
        }
        return sourcePosition
    }

    async loadRevision(revisionId: string | null) {
        const data = this.$data()
        if (data?.materialId) {
            void this.router.navigate(["materials", data.materialId, "edit", revisionId], {queryParamsHandling: "preserve"})
        }
    }

    addNode(Node: AddableNode): void {
        const center = this.$nodeEditor().viewportCenter
        // offset a little bit to the top left to account for the node size (not 100% precise)
        const spawnPosition = {x: center.x - 100, y: center.y - 200}
        const addedNode = new Node({parameters: {inputs: {}, settings: {}, ui: {position: spawnPosition}}, disableValidation: false})
        addedNode.applyDefaults()
        this.nodesService.add(addedNode)
    }

    overlayClosed() {
        void this.router.navigate([this.returnUrl.url], {
            relativeTo: this.returnUrl.relative ? this.route : undefined,
            queryParamsHandling: "preserve",
        })
    }

    navigateBack() {
        void this.$routedDialog().closeIfClean()
    }

    async saveNewMaterialRevision() {
        const materialGraph = this.$data()?.materialGraph
        if (!materialGraph) return
        // save editor position to material graph node
        materialGraph.updateParameters({editor: this.$nodeEditor().viewportParameters}, true)

        const dialogRef: MatDialogRef<SaveMaterialRevisionDialogComponent, SaveMaterialRevisionDialogResult | false> = this.dialog.open(
            SaveMaterialRevisionDialogComponent,
            {
                width: "450px",
                data: {
                    materialId: this.$data()?.materialId,
                },
            },
        )

        const result = await firstValueFrom(dialogRef.afterClosed(), {defaultValue: undefined})
        if (!result) {
            // dialog was cancelled
            return
        }

        void this.notifications.withUserFeedback(
            async () => {
                const materialId = result.materialId
                if (!materialId) {
                    throw new Error("No material id provided")
                }

                const graph = this.$data()?.materialGraph

                const {createMaterialRevisionFromGraphNode: newRevision} = await mutateThrowingErrors(this.materialEditorCreateMaterialRevision)({
                    input: {
                        materialId: materialId,
                        graphSchema: graphSchemaString(),
                        comment: `${result.comment} (Platform ${Settings.APP_VERSION})`.trim(),
                        graphNode: graph?.serialize(),
                    },
                })
                void this.router.navigate(["materials", materialId, "edit", newRevision.id], {
                    queryParamsHandling: "preserve",
                    replaceUrl: true,
                })
                this.$data.update((data) =>
                    data
                        ? {
                              ...data,
                              materialRevisionId: newRevision?.id ?? null,
                          }
                        : null,
                )
                this.$savedHash.set(this.$data()?.materialGraph?.uniqueId ?? "")
            },
            {
                success: "Material revision saved.",
                error: "Can't save new material revision.",
            },
        )
    }
}
